import { useCallback, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, MessageCircle, Moon, Phone, Zap } from 'lucide-react';
import { RangePicker, type RangeSelection } from '@/components/RangePicker';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonReports } from '@/components/Skeleton';
import { useReports } from '@/hooks/useReports';
import { useCountUp } from '@/hooks/useCountUp';
import { buildExportUrl } from '@/lib/api';
import type { ReportsMetricsV2, ReportsPeriod, ReportsRange, ReportsSnapshot } from '@/lib/api';

// 2026-06-30 (DateFilter) — presets + custom range. Default = Last 30 days
// ('month'): the fullest money picture on a proof page. Legacy 'week'
// (rolling 7d) still parses from old URLs.
const VALID_PERIODS: readonly ReportsPeriod[] = [
  'today',
  'week',
  'month',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'custom',
] as const;
const DEFAULT_PERIOD: ReportsPeriod = 'month';
const RANGE_STORAGE_KEY = 'ss_reports_range_v1';
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseSelection(params: URLSearchParams): RangeSelection {
  const raw = params.get('period');
  const period: ReportsPeriod =
    raw && (VALID_PERIODS as readonly string[]).includes(raw)
      ? (raw as ReportsPeriod)
      : DEFAULT_PERIOD;
  if (period === 'custom') {
    const from = params.get('from') ?? '';
    const to = params.get('to') ?? '';
    if (YMD_RE.test(from) && YMD_RE.test(to) && from <= to) {
      return { period, from, to };
    }
    return { period: DEFAULT_PERIOD };
  }
  return { period };
}

function readStoredSelection(): RangeSelection | null {
  try {
    const raw = localStorage.getItem(RANGE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RangeSelection;
    if (!(VALID_PERIODS as readonly string[]).includes(parsed.period)) return null;
    if (parsed.period === 'custom') {
      if (!parsed.from || !parsed.to || !YMD_RE.test(parsed.from) || !YMD_RE.test(parsed.to)) {
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

/** "…this month" phrasing for the closing money line. */
function periodPhrase(period: ReportsPeriod): string {
  switch (period) {
    case 'today':
      return 'today';
    case 'week':
      return 'in the last 7 days';
    case 'this_week':
      return 'this week';
    case 'last_week':
      return 'last week';
    case 'this_month':
      return 'this month';
    case 'last_month':
      return 'last month';
    case 'custom':
      return 'in this period';
    default:
      return 'in the last 30 days';
  }
}

/** Range portion of the export URL — custom selections carry from/to. */
function exportRangeQs(sel: RangeSelection): string {
  const sp = new URLSearchParams({ period: sel.period });
  if (sel.period === 'custom' && sel.from && sel.to) {
    sp.set('from', sel.from);
    sp.set('to', sel.to);
  }
  return sp.toString();
}

/** "Jun 2 – Jul 1, 2026" from the server-resolved tenant-local range. */
function formatRangeLabel(range?: ReportsRange): string | undefined {
  if (!range) return undefined;
  const parse = (ymd: string): Date | null => {
    if (!YMD_RE.test(ymd)) return null;
    return new Date(Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)));
  };
  const f = parse(range.from_ymd);
  const t = parse(range.to_ymd);
  if (!f || !t) return undefined;
  const md = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const y = (d: Date) => d.getUTCFullYear();
  if (range.from_ymd === range.to_ymd) return `${md(f)}, ${y(f)}`;
  if (y(f) === y(t)) return `${md(f)} – ${md(t)}, ${y(t)}`;
  return `${md(f)}, ${y(f)} – ${md(t)}, ${y(t)}`;
}

function fmtMoney(v: number): string {
  return `$${Math.round(v).toLocaleString()}`;
}

/** "Jun 2" (or "Jun 2, 2025" when not this year) from a YYYY-MM-DD. */
function formatSinceLabel(ymd: string): string {
  if (!YMD_RE.test(ymd)) return ymd;
  const d = new Date(Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)));
  const sameYear = d.getUTCFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** "42s" under a minute, "3m 20s" over, "1h 5m" over an hour. */
function fmtDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** "Sat, Jun 27" — deliberately NO clock time: `Last Quoted At` is a GHL
 *  DATE-typed field (hour unknowable), so showing "9:47 PM" would be
 *  fabricated precision. Date-level truth only. */
function formatCatchDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** v2 hero — "$X in jobs quoted this period" + lifetime line. No deltas, no
 *  gauges. Honesty lock: quoted/caught language only — never revenue/earned. */
function QuotedHero({
  m,
  phrase,
  perDay,
}: {
  m: ReportsMetricsV2;
  phrase: string;
  perDay?: ReadonlyArray<{ day: string; value: number }>;
}) {
  const animated = useCountUp(Math.round(m.quoted_value), { durationMs: 800 });
  // Subtle in-window trend BAR row (bars only — no gauges/radials by design).
  // Honest-empty: hidden under 2 points or when the window is all-zero.
  const bars = (perDay ?? []).map((p) => p.value);
  const maxBar = Math.max(0, ...bars);
  const showBars = bars.length >= 2 && maxBar > 0;
  // Axis reference — first/last day of the series so the bars read as
  // "value over these days," not floating shapes.
  const dayLabel = (ymd?: string) =>
    ymd && YMD_RE.test(ymd) ? formatSinceLabel(ymd) : null;
  const first = dayLabel(perDay?.[0]?.day);
  const last = dayLabel(perDay?.[perDay.length - 1]?.day);
  const axis = showBars && first && last ? { from: first, to: last } : null;
  return (
    <div
      className="mx-4 mt-4 animate-ss-fade-in rounded-ss-lg p-5 text-white shadow-ss-md"
      style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)' }}
    >
      <div className="text-xs font-semibold uppercase tracking-wider opacity-85">Jobs quoted</div>
      <div className="mt-1 text-[44px] font-bold leading-none tracking-tight tabular-nums">
        ${animated.toLocaleString()}
      </div>
      <div className="mt-1.5 text-[13px] opacity-90">
        {m.quotes_count > 0
          ? `${m.quotes_count} ${m.quotes_count === 1 ? 'job' : 'jobs'} quoted ${phrase}`
          : `quoted ${phrase}`}
      </div>
      {showBars ? (
        <div className="mt-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
            Quoted value per day
          </div>
          <div className="mt-1 flex h-8 items-end gap-[3px]" aria-hidden="true">
            {bars.map((v, i) => (
              <div
                key={`${i}-${v}`}
                className="flex-1 rounded-sm bg-white/30"
                style={{ height: `${v > 0 ? Math.max(12, Math.round((v / maxBar) * 100)) : 6}%` }}
              />
            ))}
          </div>
          {axis ? (
            <div className="mt-1 flex justify-between text-[10px] tabular-nums opacity-60">
              <span>{axis.from}</span>
              <span>{axis.to}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {m.lifetime_quoted_value > 0 ? (
        <div className="mt-2 text-[12px] opacity-75">
          {fmtMoney(m.lifetime_quoted_value)} quoted since day one
        </div>
      ) : null}
    </div>
  );
}

/** Per-metric accent tints — icon color + faint background wash. */
const TILE_TINTS = {
  blue: {
    chip: 'bg-ss-blue/10 text-ss-blue dark:bg-ss-blue/20',
    wash: 'bg-white dark:bg-[#1E293B]',
  },
  green: {
    chip: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300',
    wash: 'bg-emerald-50/50 dark:bg-emerald-950/20',
  },
  amber: {
    chip: 'bg-amber-500/10 text-amber-600 dark:bg-amber-400/15 dark:text-amber-300',
    wash: 'bg-amber-50/50 dark:bg-amber-950/20',
  },
  violet: {
    chip: 'bg-violet-500/10 text-violet-600 dark:bg-violet-400/15 dark:text-violet-300',
    wash: 'bg-violet-50/40 dark:bg-violet-950/20',
  },
  night: {
    chip: 'bg-indigo-500/10 text-indigo-500 dark:bg-indigo-400/15 dark:text-indigo-300',
    wash: 'bg-indigo-50/40 dark:bg-indigo-950/25',
  },
} as const;
type TileTint = keyof typeof TILE_TINTS;

/** Estimate-mode hero (hero_mode === 'estimates'). Honesty locks: "Estimates
 *  booked" ONLY when confirmable (native calendar — Phase 3); otherwise the
 *  auto-captured floor, "booking links sent to qualified leads". Never
 *  revenue/ROI/invented dollars in estimate mode. */
function EstimateHero({ m, phrase }: { m: ReportsMetricsV2; phrase: string }) {
  const confirmed = m.estimates_booked_available === true && m.estimates_booked != null;
  const value = confirmed ? (m.estimates_booked as number) : (m.booking_links_sent ?? 0);
  const animated = useCountUp(value, { durationMs: 800 });
  const bars = (m.links_per_day ?? []).map((p) => p.value);
  const maxBar = Math.max(0, ...bars);
  const showBars = !confirmed && bars.length >= 2 && maxBar > 0;
  return (
    <div
      className="mx-4 mt-4 animate-ss-fade-in rounded-ss-lg p-5 text-white shadow-ss-md"
      style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)' }}
    >
      <div className="text-xs font-semibold uppercase tracking-wider opacity-85">
        {confirmed ? 'Estimates booked' : 'Booking links sent'}
      </div>
      <div className="mt-1 text-[44px] font-bold leading-none tracking-tight tabular-nums">
        {animated.toLocaleString()}
      </div>
      <div className="mt-1.5 text-[13px] opacity-90">
        {confirmed
          ? `estimates booked ${phrase}`
          : `booking links sent to qualified leads ${phrase}`}
      </div>
      {showBars ? (
        <div className="mt-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
            Links sent per day
          </div>
          <div className="mt-1 flex h-8 items-end gap-[3px]" aria-hidden="true">
            {bars.map((v, i) => (
              <div
                key={`${i}-${v}`}
                className="flex-1 rounded-sm bg-white/30"
                style={{ height: `${v > 0 ? Math.max(12, Math.round((v / maxBar) * 100)) : 6}%` }}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Big-numeral stat tile — icon chip + tinted wash; count-up on integer
 *  values; tappable drill-down when `to` is set. VISUAL ONLY — the numbers
 *  are the canonical metrics untouched. */
function StatTile({
  value,
  countTo,
  label,
  sub,
  to,
  icon,
  tint = 'blue',
}: {
  value?: string;
  /** When set, the numeral counts up on mount (integers only). */
  countTo?: number;
  label: string;
  sub?: string;
  to?: string;
  icon?: React.ReactNode;
  tint?: TileTint;
}) {
  const animated = useCountUp(countTo ?? 0, { durationMs: 700 });
  const shown = countTo != null ? animated.toLocaleString() : (value ?? '');
  const t = TILE_TINTS[tint];
  const body = (
    <div
      className={`h-full animate-ss-fade-in rounded-ss-md border border-ink-200 dark:border-[#334155] ${t.wash} p-4 shadow-ss-sm`}
    >
      {icon ? (
        <span className={`mb-2 inline-flex h-8 w-8 items-center justify-center rounded-full ${t.chip}`}>
          {icon}
        </span>
      ) : null}
      <div className="text-[30px] font-bold leading-tight tabular-nums text-ink-900 dark:text-[#E2E8F0]">
        {shown}
      </div>
      <div className="mt-0.5 text-[13px] font-semibold text-ink-700 dark:text-[#CBD5E1]">{label}</div>
      {sub ? (
        <div className="mt-0.5 text-[11px] leading-snug text-ink-500 dark:text-[#94A3B8]">{sub}</div>
      ) : null}
    </div>
  );
  return to ? (
    <Link to={to} className="ss-press block h-full">
      {body}
    </Link>
  ) : (
    body
  );
}

/** Wow callouts — real timestamps + amounts only; the specificity IS the wow. */
function Callouts({ m, phrase }: { m: ReportsMetricsV2; phrase: string }) {
  const lines: string[] = [];
  for (const c of m.callouts) {
    if (c.kind === 'big_catch') {
      // Honesty rule: a QUOTE is "Quoted", never "Caught" — caught/recovered
      // is reserved for genuine recoveries (missed-call text-backs). Never
      // revenue/earned/booked unless booking is confirmed.
      lines.push(
        `Quoted a ${fmtMoney(c.amount)} job ${formatCatchDate(c.at_ms)}${c.after_hours ? ' — after hours' : ''}.`,
      );
    } else if (c.kind === 'fast_recovery') {
      lines.push(`Shelby texts back in ~${c.seconds}s.`);
    } else if (c.kind === 'window_total') {
      lines.push(`${fmtMoney(c.amount)} quoted ${phrase} across ${c.quotes} jobs.`);
    }
  }
  if (lines.length === 0) return null;
  const [first, ...rest] = lines.slice(0, 3);
  return (
    <div className="mx-4 mt-3 animate-ss-slide-up rounded-ss-lg border border-ss-blue/25 bg-ss-blue-light dark:bg-[#1E3A5F] p-4 shadow-ss-sm">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ss-blue/15 text-ss-blue dark:bg-ss-blue/25 dark:text-[#93C5FD]">
          <Zap size={18} strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-bold leading-snug text-ink-900 dark:text-[#DBEAFE]">{first}</p>
          {rest.map((l) => (
            <p key={l} className="mt-1 text-[12.5px] font-medium text-ink-700 dark:text-[#BFDBFE]">
              {l}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Reports — money/loss/attribution rebuild (2026-06-30, GPT-refined spec).
 * Hierarchy: money caught → money almost lost (recovery) → speed brag →
 * value multiple (client tenants only) → lifetime → biggest win → labor
 * relief → money trend → end on money → export.
 *
 * HONESTY GUARDS (locked): "caught"/"quoted opportunities" language only —
 * never revenue/saved/closed/ROI. "Value multiple", not ROI, with the
 * quoted-not-booked caveat. "Lifetime caught", not banked. No industry
 * benchmarks except the widely-used 5-minute speed-to-lead line. Every
 * number auto-captured; the no-manual-entry microcopy is the trust signal.
 */
export function Reports() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selection = parseSelection(searchParams);

  const handleRangeChange = useCallback(
    (next: RangeSelection) => {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (next.period === DEFAULT_PERIOD) sp.delete('period');
          else sp.set('period', next.period);
          if (next.period === 'custom' && next.from && next.to) {
            sp.set('from', next.from);
            sp.set('to', next.to);
          } else {
            sp.delete('from');
            sp.delete('to');
          }
          return sp;
        },
        { replace: true },
      );
      try {
        localStorage.setItem(RANGE_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // storage full/blocked — persistence is a nice-to-have
      }
    },
    [setSearchParams],
  );

  // On first mount with a bare URL, restore the last-chosen range (cheap
  // localStorage persistence across app opens). URL params always win.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (searchParams.get('period')) return;
    const stored = readStoredSelection();
    if (stored && stored.period !== DEFAULT_PERIOD) handleRangeChange(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportsQuery = useReports(slug, selection.period, selection.from, selection.to);

  // Freshness trust signal next to the range label.
  const ageMs = reportsQuery.dataUpdatedAt ? Date.now() - reportsQuery.dataUpdatedAt : null;
  const updatedLabel =
    ageMs == null
      ? undefined
      : ageMs < 90_000
        ? 'Updated just now'
        : `Updated ${Math.round(ageMs / 60_000)}m ago`;

  const snap: ReportsSnapshot | undefined = reportsQuery.data;
  const rangeLabel = formatRangeLabel(snap?.range);

  // --- Render ------------------------------------------------------------
  return (
    <div
      className="h-full min-h-0 overflow-y-auto bg-ink-50 dark:bg-[#0B1220] pb-6"
      style={{
        WebkitOverflowScrolling: 'touch',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Centered column so desktop doesn't stretch the mobile-first layout. */}
      <div className="mx-auto w-full max-w-xl">
        <div className="px-5 pb-1 pt-4">
          <h2 className="mb-2 text-[22px] font-bold tracking-tight text-ink-900 dark:text-[#E2E8F0]">
            Reports
          </h2>
          <RangePicker
            value={selection}
            onChange={handleRangeChange}
            resolvedLabel={
              rangeLabel
                ? `${rangeLabel}${updatedLabel ? ` · ${updatedLabel}` : ''}`
                : updatedLabel
            }
          />
        </div>

        {reportsQuery.isLoading ? (
          <SkeletonReports />
        ) : reportsQuery.isError || !snap ? (
          <ErrorState
            title="Couldn’t load reports"
            message={
              reportsQuery.error instanceof Error
                ? reportsQuery.error.message
                : 'Please try again.'
            }
            onRetry={() => void reportsQuery.refetch()}
          />
        ) : (
          <>
            {(() => {
              const m = snap.metrics_v2;
              const phrase = periodPhrase(selection.period);
              if (!m) {
                return (
                  <p className="mx-4 mt-6 text-center text-sm text-ink-500 dark:text-[#94A3B8]">
                    Metrics are updating — pull to refresh in a moment.
                  </p>
                );
              }
              return (
                <>
                  {/* 1 — HERO: tenant hero metric. Estimate tenants lead with
                      confirmed bookings when available, else the honest floor
                      "booking links sent"; quote tenants (DP) render exactly
                      as before. */}
                  {m.hero_mode === 'estimates' ? (
                    <EstimateHero m={m} phrase={phrase} />
                  ) : (
                    <QuotedHero m={m} phrase={phrase} perDay={snap.quote_value_per_day} />
                  )}

                  {/* Wow callouts — real amounts/dates only */}
                  <Callouts m={m} phrase={phrase} />

                  {/* 2+3 — calls answered | texts answered */}
                  <div className="grid grid-cols-2 gap-2 px-4 pt-4">
                    <StatTile
                      countTo={m.calls_answered}
                      label={`call${m.calls_answered === 1 ? '' : 's'} answered`}
                      to={`/${slug}/conversations`}
                      icon={<Phone size={16} strokeWidth={2.2} />}
                      tint="blue"
                    />
                    <StatTile
                      countTo={m.texts_answered}
                      label={`text${m.texts_answered === 1 ? '' : 's'} answered`}
                      to={`/${slug}/conversations`}
                      icon={<MessageCircle size={16} strokeWidth={2.2} />}
                      tint="green"
                    />
                  </div>

                  {/* 4+5 — missed recovered | reply speed (Shelby-only sample) */}
                  <div className="grid grid-cols-2 gap-2 px-4 pt-2">
                    {m.missed_recovered > 0 ? (
                      <StatTile
                        countTo={m.missed_recovered}
                        label={`missed call${m.missed_recovered === 1 ? '' : 's'} texted back`}
                        sub="in seconds, automatically"
                        to={`/${slug}/conversations`}
                        icon={<Zap size={16} strokeWidth={2.2} />}
                        tint="amber"
                      />
                    ) : null}
                    {m.avg_reply_sec != null ? (
                      <StatTile
                        value={fmtDuration(m.avg_reply_sec)}
                        label="avg text reply"
                        sub="Shelby's replies · industry speed-to-lead benchmark is 5 minutes"
                        icon={<Clock size={16} strokeWidth={2.2} />}
                        tint="violet"
                      />
                    ) : null}
                  </div>

                  {/* 6 — after-hours strip */}
                  {m.after_hours.handled > 0 ? (
                    <div className={`mx-4 mt-2 animate-ss-fade-in rounded-ss-md border border-ink-200 dark:border-[#334155] ${TILE_TINTS.night.wash} p-4 shadow-ss-sm`}>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${TILE_TINTS.night.chip}`}>
                          <Moon size={16} strokeWidth={2.2} />
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-[28px] font-bold leading-none tabular-nums text-ink-900 dark:text-[#E2E8F0]">
                              {m.after_hours.handled}
                            </span>
                            <span className="text-[13px] font-semibold text-ink-700 dark:text-[#CBD5E1]">
                              handled after you closed
                            </span>
                          </div>
                          {m.after_hours.quoted_value > 0 ? (
                            <p className="mt-1 text-[12px] text-ink-500 dark:text-[#94A3B8]">
                              {fmtMoney(m.after_hours.quoted_value)} quoted after hours
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* 7 — bookings row (links line only when honestly countable) */}
                  {m.bookings.booked > 0 || (m.bookings.links_sent ?? 0) > 0 ? (
                    <div className="grid grid-cols-2 gap-2 px-4 pt-2">
                      {(m.bookings.links_sent ?? 0) > 0 ? (
                        <StatTile
                          countTo={m.bookings.links_sent ?? 0}
                          label={`booking link${m.bookings.links_sent === 1 ? '' : 's'} sent`}
                          icon={<Calendar size={16} strokeWidth={2.2} />}
                          tint="violet"
                        />
                      ) : null}
                      {m.bookings.booked > 0 ? (
                        <StatTile
                          countTo={m.bookings.booked}
                          label="booked"
                          sub="marked booked by you"
                          to={`/${slug}/conversations?filter=booked`}
                          icon={<Calendar size={16} strokeWidth={2.2} />}
                          tint="green"
                        />
                      ) : null}
                    </div>
                  ) : null}

                  {/* 8 — lifetime conversations footer (retention rally-cry) */}
                  {m.lifetime_conversations > 0 ? (
                    <p className="mx-4 mb-1 mt-5 px-1 text-center text-[13px] font-semibold text-ink-700 dark:text-[#CBD5E1]">
                      {m.lifetime_conversations.toLocaleString()} conversations handled for you
                      {snap.lifetime.since_ymd
                        ? ` since ${formatSinceLabel(snap.lifetime.since_ymd)}`
                        : ' since you started'}
                      .
                    </p>
                  ) : null}
                </>
              );
            })()}

            {/* The single page-level trust line — every per-card trust-tag
                was removed in favor of this one (ReportsTighten). */}
            <p className="mx-4 mb-3 px-1 text-center text-[11px] text-ink-400 dark:text-[#64748B]">
              All figures auto-tracked from your calls, texts, and missed-call log — nothing
              entered by hand.
            </p>

            {/* Truncation note — the counts are a floor, never silently short. */}
            {snap.truncated ? (
              <div className="mx-4 mb-4 rounded-ss-md border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
                This window has more conversations than we can scan in one report.
                The numbers above cover the most recent activity and may undercount
                the full period.
              </div>
            ) : null}

            {/* Export — CSV downloads, PDF opens an HTML report that auto-fires
                window.print() so the owner can Save as PDF from the print
                dialog. No new npm deps, no headless renderer needed. */}
            <div className="mx-4 mb-4 rounded-ss-md border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#1E293B] p-4 shadow-ss-sm">
              <div className="mb-2 text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]">
                Export this report
              </div>
              <p className="mb-3 text-xs text-ink-500 dark:text-[#94A3B8]">
                Save the current view as CSV or open a printable HTML report you can save as PDF.
              </p>
              <div className="flex gap-2">
                <a
                  href={buildExportUrl(`/pwa/reports/export?slug=${encodeURIComponent(slug)}&${exportRangeQs(selection)}&format=csv`)}
                  download
                  className="ss-press flex-1 rounded-xl border border-ink-200 dark:border-[#334155] bg-ink-50 dark:bg-[#0F172A] px-3 py-2.5 text-center text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]"
                >
                  Export CSV
                </a>
                <a
                  href={buildExportUrl(`/pwa/reports/export?slug=${encodeURIComponent(slug)}&${exportRangeQs(selection)}&format=pdf`)}
                  target="_blank"
                  rel="noreferrer"
                  className="ss-press flex-1 rounded-xl px-3 py-2.5 text-center text-sm font-semibold text-[color:var(--ss-accent-fg,white)]"
                  style={{ background: 'var(--ss-accent, #2563EB)' }}
                >
                  Export PDF
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
